import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ScrollToTop from "@/components/ScrollToTop";
import PWAInstallPrompt from "@/components/PWAInstallPrompt";
import { SessionTimeoutManager } from "@/components/SessionTimeoutManager";
import { RouteSeoManager } from "@/components/RouteSeoManager";
import HomePage from "@/pages/HomePage";
import VendorsPage from "@/pages/VendorsPage";
import VendorDetailPage from "@/pages/VendorDetailPage";
import CategoryPage from "@/pages/CategoryPage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import DashboardPage from "@/pages/DashboardPage";
import AdminPage from "@/pages/AdminPage";
import BlogPage from "@/pages/BlogPage";
import BlogPostPage from "@/pages/BlogPostPage";
import AuthPage from "@/pages/AuthPage";
import ClientAuthPage from "@/pages/ClientAuthPage";
import VendorAuthPage from "@/pages/VendorAuthPage";
import VendorDashboard from "@/pages/VendorDashboard";
import VendorPricingPage from "@/pages/VendorPricingPage";
import WeddingPlannerPage from "@/pages/WeddingPlannerPage";
import GettingMarriedPage from "@/pages/GettingMarriedPage";
import GibraltarWeddingGuidePage from "@/pages/GibraltarWeddingGuidePage";
import CostGettingMarriedPage from "@/pages/CostGettingMarriedPage";
import ElopeInGibraltarPage from "@/pages/ElopeInGibraltarPage";
import FreeGuidePage from "@/pages/FreeGuidePage";
import GuestGalleryUploadPage from "@/pages/GuestGalleryUploadPage";
import WeddingWebsitePublicPage from "@/pages/WeddingWebsitePublicPage";
import WeddingTemplatePreviewGalleryPage from "@/pages/WeddingTemplatePreviewGalleryPage";
import WeddingTemplateV2GalleryPage from "@/pages/WeddingTemplateV2GalleryPage";
import WeddingTemplateV2PreviewPage from "@/pages/WeddingTemplateV2PreviewPage";
import AboutGibraltarPage from "@/pages/AboutGibraltarPage";
import AboutExpoPage from "@/pages/AboutExpoPage";
import PasswordResetPage from "@/pages/PasswordResetPage";
import CivilRegistryWeddingsPage from "@/pages/CivilRegistryWeddingsPage";

function AppContent() {
  const location = useLocation();
  const hideNavFooter =
    location.pathname.startsWith('/vendor/dashboard') ||
    location.pathname.startsWith('/wedding/') ||
    location.pathname.startsWith('/weddings/') ||
    location.pathname.startsWith('/wedding-v2/') ||
    location.pathname.startsWith('/guest-gallery/');
  
  return (
    <>
      <ScrollToTop />
      <SessionTimeoutManager />
      <RouteSeoManager />
      {!hideNavFooter && <Navbar />}
      <main className="min-h-screen">
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/vendors" element={<VendorsPage />} />
          <Route path="/vendors/:id" element={<VendorDetailPage />} />
          <Route path="/guest-gallery/:slug/upload" element={<GuestGalleryUploadPage />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:slug" element={<BlogPostPage />} />
          <Route path="/getting-married-in-gibraltar" element={<GettingMarriedPage />} />
          <Route path="/how-to-get-married-in-gibraltar" element={<GibraltarWeddingGuidePage />} />
          <Route path="/cost-of-getting-married-in-gibraltar" element={<CostGettingMarriedPage />} />
          <Route path="/civil-registry-weddings-gibraltar" element={<CivilRegistryWeddingsPage />} />
          <Route path="/elope-in-gibraltar" element={<ElopeInGibraltarPage />} />
          <Route path="/free-guide" element={<FreeGuidePage />} />
          <Route path="/wedding/:slug" element={<WeddingWebsitePublicPage />} />
          <Route path="/weddings/:slug/gallery" element={<GuestGalleryUploadPage mode="public-gallery" />} />
          <Route path="/weddings/:slug" element={<WeddingWebsitePublicPage />} />
          <Route path="/wedding-template-previews" element={<WeddingTemplatePreviewGalleryPage />} />
          <Route path="/wedding-template-previews-v2" element={<WeddingTemplateV2GalleryPage />} />
          <Route path="/wedding-v2/:templateKey" element={<WeddingTemplateV2PreviewPage />} />
          <Route path="/about-gibraltar" element={<AboutGibraltarPage />} />
          <Route path="/about-expo" element={<AboutExpoPage />} />
          <Route path="/:categorySlug" element={<CategoryPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/auth" element={<Navigate to="/auth/select" replace />} />
          <Route path="/auth/select" element={<AuthPage />} />
          <Route path="/client/auth" element={<ClientAuthPage />} />
          <Route path="/reset-password" element={<PasswordResetPage accountType="client" />} />
          <Route path="/vendor/auth" element={<VendorAuthPage />} />
          <Route path="/vendor/reset-password" element={<PasswordResetPage accountType="vendor" />} />
          <Route path="/vendor/pricing" element={<VendorPricingPage />} />
          <Route path="/planner" element={<WeddingPlannerPage />} />
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/admin" element={<AdminPage />} />
          <Route path="/vendor/dashboard" element={<VendorDashboard />} />
        </Routes>
      </main>
      {!hideNavFooter && <Footer />}
      <PWAInstallPrompt />
    </>
  );
}

function App() {
  return (
    <AuthProvider>
      <div className="App noise-overlay">
        <BrowserRouter>
          <AppContent />
        </BrowserRouter>
        <Toaster position="bottom-right" />
      </div>
    </AuthProvider>
  );
}

export default App;
